import { ApiCall } from '../store/api/api-call';
import { retrieveToken } from '../auth-token';
import { getAuthApiPing, parentFrameUrl } from '../store/api/auth-api';
import { LoginResponse } from '../survey-tool/declarations/auth-api';
import { authApiHost } from '../app-config';

// https://developers.facebook.com/docs/permissions/reference
// Every permission requires App Review except for email and public_profile.
export const DEFAULT_FB_SCOPE = 'email,public_profile';

const loadFacebookScripts = (): Promise<void> => {
  const w = window as any as {
    fbSdkLoaded?: Promise<void>;
    fbAsyncInit?: () => void;
  };
  if (w.fbSdkLoaded) {
    return w.fbSdkLoaded;
  }
  w.fbSdkLoaded = new Promise((resolve) => {
    w.fbAsyncInit = resolve;
  });
  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.crossOrigin = 'anonymous';
  script.src = `https://connect.facebook.net/en_US/sdk.js`;
  document.body.appendChild(script);
  return w.fbSdkLoaded;
};

const _getFbGlobalObject: Record<string, FbGlobalObject | undefined> = {};

const getFbGlobalObject = async (appId: string) => {
  if (!_getFbGlobalObject[appId]) {
    await loadFacebookScripts();
    const fb = (window as any).FB as FbGlobalObject;
    fb.init({
      appId,
      version: 'v2.7',
    });
    _getFbGlobalObject[appId] = fb;
  }
  return _getFbGlobalObject[appId]!;
};

interface FbGlobalObject {
  init: (options: { appId: string; version: 'v2.7' }) => void;
  getLoginStatus: (callback: (loginStatus: FbLoginStatus) => void, options: { scope: typeof DEFAULT_FB_SCOPE }) => void;
}

type FbLoginStatus = FbLoginStatusConnected | FbLoginStatusOther;

interface FbLoginStatusConnected {
  status: 'connected';
  authResponse: {
    userID: string;
    accessToken: string;
  };
}

interface FbLoginStatusOther {
  status: 'unknown';
}

export const getFbApi = async (appId: string) => {
  if (!appId) {
    throw new Error(`getFbApi(): appId is required argument!`);
  }
  const fb = await getFbGlobalObject(appId);
  return {
    appId,
    getLoginStatus: () =>
      new Promise((resolve: (result: FbLoginStatus) => void) => {
        fb.getLoginStatus(resolve, { scope: DEFAULT_FB_SCOPE });
      }),
    fetchState: async (): Promise<string | null> => {
      try {
        const response = await ApiCall({
          method: 'GET',
          url: `${authApiHost}/oauth/facebook/get-state`,
        });
        const result = response.data as {
          state: string;
        };
        return result.state;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        return null;
      }
    },
  };
};

export const signInOrConnectFacebook = async ({
  code,
  token,
  platformKey: _platformKey,
}: {
  code?: string;
  token?: string;
  platformKey?: string;
}): Promise<void> => {
  let mode: 'sign-in' | 'connect';
  let platformKey = _platformKey;

  const ppToken = retrieveToken();
  if (!ppToken) {
    mode = 'sign-in';
  } else {
    const pingResponse = await getAuthApiPing();
    if (pingResponse?.ppUuid) {
      mode = 'connect';
      platformKey = pingResponse.platformKey;
    } else {
      mode = 'sign-in';
    }
  }

  const method = 'POST';
  const url =
    mode === 'sign-in'
      ? `${authApiHost}/oauth/facebook/id-token/sign-in`
      : `${authApiHost}/oauth/facebook/id-token/connect`;
  const headers =
    mode === 'sign-in' ? ({} as Record<string, string>) : ({ 'pp-token': ppToken } as Record<string, string>);
  const payload = { code, token, platformKey, from: 'pureprofile-app-fb-login', parentFrameUrl };
  await ApiCall<LoginResponse>({
    method,
    url,
    headers,
    data: payload,
  });
};
